import cn from 'classnames';
import { useMemo, useState } from 'react';
import { InteractiveSpan } from '@Components/InteractiveEl';
import { ToggleSwitch } from '@Components/ToggleSwitch';
import { DivergentColorMap20, makeSteppedHeatMap } from '@src/utils/colors';
import {
  BASELINE_DISPLAY_NAME,
  BASELINE_GROUP_KEY,
  BASELINE_TOGGLE_LABEL,
  DARK_CHANNEL_TOGGLE_LABEL
} from '../dtc-commerce-mmm-constants';
import { DashboardData, HierarchyLevel, HierarchyLevelConst } from '../dtc-commerce-mmm-types';
import { aggregateDualChannels, DualChannel } from '../hooks/useChannelAggregation';
import {
  formatCurrency,
  formatDollarsExact,
  formatPercentage,
  formatWholeNumber
} from '../utils/dtc-commerce-mmm-format';
import { ChannelIcon } from './ChannelIcon';
import { IncrementalityBeaker } from './IncrementalityBeaker';
import { IncrementalityNote } from './IncrementalityNote';
import { MediaTypeBadge, MediaTypeBadgeVariant } from './MediaTypeBadge';
import { PerformanceTableShell } from './PerformanceTableShell';

const formatRevenueValue = (value: number, isRevenue: boolean): string =>
  isRevenue ? formatCurrency(value) : formatWholeNumber(value);

const formatEfficiency = (value: number | null): string => formatDollarsExact(value);

const SortColumn = {
  CHANNEL: 'channel',
  SPEND: 'spend',
  EFFECT: 'effect',
  SPEND_PCT: 'spendPct',
  EFFECT_PCT: 'effectPct',
  EFFICIENCY: 'efficiency'
} as const;
type SortColumn = (typeof SortColumn)[keyof typeof SortColumn];

type SortDir = 'asc' | 'desc';

interface ChannelPerformanceTableProps {
  data: DashboardData;
  isRevenue: boolean;
  hierarchyLevel: HierarchyLevel;
  onChannelClick: (groupKey: string, focusedFeatureName?: string) => void;
}

export const ChannelPerformanceTable = ({
  data,
  isRevenue,
  hierarchyLevel,
  onChannelClick
}: ChannelPerformanceTableProps) => {
  const [sortColumn, setSortColumn] = useState<SortColumn>(SortColumn.SPEND);
  const [sortDir, setSortDir] = useState<SortDir>('desc');
  const [showDark, setShowDark] = useState(false);
  const [showDirectChange, setShowDirectChange] = useState(false);
  const [showBaseline, setShowBaseline] = useState(false);

  const baselineTotal = useMemo(
    () => data.actualVsPredictedT12M.reduce((s, r) => s + r.baselinePrediction, 0),
    [data.actualVsPredictedT12M]
  );

  const dual = useMemo(() => {
    const channels = aggregateDualChannels(
      data.actualized.cptbT12M,
      data.raw.cptbT12M,
      hierarchyLevel,
      isRevenue
    );

    if (!showBaseline || baselineTotal <= 0) return channels;

    const baseline: DualChannel = {
      groupKey: BASELINE_GROUP_KEY,
      displayName: BASELINE_DISPLAY_NAME,
      channel: BASELINE_GROUP_KEY,
      platform: '',
      mediaType: '',
      isDark: false,
      hasIncrementalityTest: false,
      directSpend: 0,
      actualizedSpend: 0,
      directContribution: baselineTotal,
      actualizedContribution: baselineTotal,
      actualizedContributionCiLo: 0,
      actualizedContributionCiHi: 0,
      directSpendShare: 0,
      actualizedSpendShare: 0,
      directContributionShare: 0,
      actualizedContributionShare: 0,
      efficiencyActualized: null,
      efficiencyDirect: null
    };

    const all = [...channels, baseline];
    const totalDirectContrib = all.reduce((s, c) => s + c.directContribution, 0);
    const totalActualizedContrib = all.reduce((s, c) => s + c.actualizedContribution, 0);
    return all.map(c => ({
      ...c,
      directContributionShare:
        totalDirectContrib > 0 ? (c.directContribution / totalDirectContrib) * 100 : 0,
      actualizedContributionShare:
        totalActualizedContrib > 0 ? (c.actualizedContribution / totalActualizedContrib) * 100 : 0
    }));
  }, [data, hierarchyLevel, isRevenue, showBaseline, baselineTotal]);

  const hasDarkChannels = useMemo(() => dual.some(c => c.isDark), [dual]);

  const visible = useMemo(() => (showDark ? dual : dual.filter(c => !c.isDark)), [dual, showDark]);

  const toggleSort = (col: SortColumn) => {
    if (sortColumn === col) {
      setSortDir(prev => (prev === 'desc' ? 'asc' : 'desc'));
      return;
    }
    setSortColumn(col);
    setSortDir(col === SortColumn.CHANNEL ? 'asc' : 'desc');
  };

  const sorted = useMemo(() => {
    const dir = sortDir === 'asc' ? 1 : -1;
    const list = [...visible];
    list.sort((a, b) => {
      let cmp: number;
      switch (sortColumn) {
        case SortColumn.CHANNEL:
          cmp = a.displayName.localeCompare(b.displayName);
          break;
        case SortColumn.SPEND:
          cmp = a.actualizedSpend - b.actualizedSpend;
          break;
        case SortColumn.EFFECT:
          cmp = a.actualizedContribution - b.actualizedContribution;
          break;
        case SortColumn.SPEND_PCT:
          cmp = a.actualizedSpendShare - b.actualizedSpendShare;
          break;
        case SortColumn.EFFECT_PCT:
          cmp = a.actualizedContributionShare - b.actualizedContributionShare;
          break;
        case SortColumn.EFFICIENCY: {
          const aVal = a.efficiencyActualized ?? (isRevenue ? -Infinity : Infinity);
          const bVal = b.efficiencyActualized ?? (isRevenue ? -Infinity : Infinity);
          cmp = isRevenue ? aVal - bVal : bVal - aVal;
          break;
        }
        default:
          cmp = a.actualizedSpend - b.actualizedSpend;
      }
      return cmp * dir;
    });
    return list;
  }, [visible, sortColumn, sortDir, isRevenue]);

  const efficiencyHeatMap = useMemo(() => {
    const values = visible
      .map(ch => ch.efficiencyActualized)
      .filter((v): v is number => v !== null && Number.isFinite(v));
    if (values.length < 2) return null;
    const min = Math.min(...values);
    const max = Math.max(...values);
    if (min === max) return null;
    // Anchor the midpoint to the blended efficiency (spend-weighted) so cells
    // read as "above / below blended" rather than "above / below the
    // halfway-point of min and max". Baseline is excluded from the blended ROAS.
    const channelsOnly = visible.filter(c => c.groupKey !== BASELINE_GROUP_KEY);
    const totalSpend = channelsOnly.reduce((s, c) => s + c.actualizedSpend, 0);
    const totalContribution = channelsOnly.reduce((s, c) => s + c.actualizedContribution, 0);
    let blended: number | undefined;
    if (isRevenue && totalSpend > 0) {
      blended = totalContribution / totalSpend;
    } else if (!isRevenue && totalContribution > 0) {
      blended = totalSpend / totalContribution;
    }
    // Clamp the midpoint into [min, max] so the gradient stays two-sided even
    // when one channel's outlier ROAS pulls the blended outside the visible range.
    const midpoint = blended !== undefined ? Math.max(min, Math.min(max, blended)) : undefined;
    // Revenue efficiency (ROAS) is better when higher, so the ramp reads min -> max as authored;
    // cost efficiency (CPX) is better when lower, so it reads backwards and shades green at the
    // low end of the domain.
    return makeSteppedHeatMap({
      min,
      max,
      midpoint,
      stops: isRevenue ? DivergentColorMap20 : [...DivergentColorMap20].reverse()
    });
  }, [visible, isRevenue]);

  const totals = useMemo(() => {
    const channelsOnly = visible.filter(c => c.groupKey !== BASELINE_GROUP_KEY);
    const actSpend = channelsOnly.reduce((s, c) => s + c.actualizedSpend, 0);
    const actContribution = channelsOnly.reduce((s, c) => s + c.actualizedContribution, 0);
    let actEfficiency: number | null = null;
    if (isRevenue && actSpend > 0) {
      actEfficiency = actContribution / actSpend;
    } else if (!isRevenue && actContribution > 0) {
      actEfficiency = actSpend / actContribution;
    }
    return { actSpend, actContribution, actEfficiency };
  }, [visible, isRevenue]);

  const kpiLabel = isRevenue ? 'Revenue' : 'Conv.';
  const efficiencyLabel = isRevenue ? 'ROAS' : 'CPA';

  const signed = (v: number, formatter: (n: number) => string): string => {
    let sign = '';
    if (v > 0) {
      sign = '+';
    } else if (v < 0) {
      sign = '−';
    }
    return `${sign}${formatter(Math.abs(v))}`;
  };
  const changeCurrency = (actual: number, direct: number): string | null =>
    showDirectChange ? signed(actual - direct, formatCurrency) : null;
  const changePercent = (actual: number, direct: number): string | null =>
    showDirectChange ? signed(actual - direct, v => formatPercentage(v, 1)) : null;
  const changeRevenue = (actual: number, direct: number): string | null =>
    showDirectChange ? signed(actual - direct, v => formatRevenueValue(v, isRevenue)) : null;
  const changeEfficiency = (actual: number | null, direct: number | null): string | null => {
    if (!showDirectChange) return null;
    if (actual === null || direct === null) return '—';
    return signed(actual - direct, formatEfficiency);
  };
  const groupColumnLabel = (() => {
    if (hierarchyLevel === HierarchyLevelConst.PLATFORM) return 'Platform';
    if (hierarchyLevel === HierarchyLevelConst.BRAND_NONBRAND) return 'Tactic';
    return 'Channel';
  })();

  const metricHeaders: { col: SortColumn; label: string }[] = [
    { col: SortColumn.SPEND, label: 'Spend' },
    { col: SortColumn.SPEND_PCT, label: 'Spend %' },
    { col: SortColumn.EFFECT, label: kpiLabel },
    { col: SortColumn.EFFECT_PCT, label: `${kpiLabel} %` },
    { col: SortColumn.EFFICIENCY, label: efficiencyLabel }
  ];

  const toggles = (
    <div className='channel-performance-table-toggles'>
      <IncrementalityNote />
      <ToggleSwitch
        label={BASELINE_TOGGLE_LABEL}
        checked={showBaseline}
        onChange={setShowBaseline}
      />
      <ToggleSwitch
        label='Show change from direct attribution values'
        checked={showDirectChange}
        onChange={setShowDirectChange}
      />
      <ToggleSwitch
        label={DARK_CHANNEL_TOGGLE_LABEL}
        checked={showDark}
        onChange={setShowDark}
        disabled={!hasDarkChannels}
      />
    </div>
  );

  return (
    <PerformanceTableShell belowTable={toggles}>
      <thead>
        <tr>
          <th className={cn('align-left', { active: sortColumn === SortColumn.CHANNEL })}>
            <InteractiveSpan onClick={() => toggleSort(SortColumn.CHANNEL)}>
              {groupColumnLabel}
              {sortColumn === SortColumn.CHANNEL && (sortDir === 'desc' ? ' ↓' : ' ↑')}
            </InteractiveSpan>
          </th>
          <th className='align-left'>Type</th>
          {metricHeaders.map(h => {
            const active = sortColumn === h.col;
            return (
              <th key={h.col} className={cn('align-center', { active })}>
                <InteractiveSpan onClick={() => toggleSort(h.col)}>
                  {h.label}
                  {active && (sortDir === 'desc' ? ' ↓' : ' ↑')}
                </InteractiveSpan>
              </th>
            );
          })}
        </tr>
      </thead>
      <tbody>
        {sorted.map(ch => {
          const isBaseline = ch.groupKey === BASELINE_GROUP_KEY;
          const dimmed = ch.isDark;
          if (isBaseline) {
            return (
              <tr key={ch.groupKey} className='baseline-row'>
                <td className='align-left'>
                  <span className='channel-name-cell'>
                    <span className='baseline-icon-spacer' aria-hidden='true' />
                    <span className='baseline-name'>{ch.displayName}</span>
                  </span>
                </td>
                <td className='align-left' aria-hidden='true' />
                <td className='align-center tabular'>
                  <MetricCell actualized='—' change={null} />
                </td>
                <td className='align-center tabular'>
                  <MetricCell actualized='—' change={null} />
                </td>
                <td className='align-center tabular'>
                  <MetricCell
                    actualized={formatRevenueValue(ch.actualizedContribution, isRevenue)}
                    change={null}
                  />
                </td>
                <td className='align-center tabular'>
                  <MetricCell
                    actualized={formatPercentage(ch.actualizedContributionShare, 1)}
                    change={null}
                  />
                </td>
                <td className='align-center tabular'>
                  <MetricCell actualized='—' change={null} />
                </td>
              </tr>
            );
          }
          return (
            <tr
              key={ch.groupKey}
              className={cn({ dimmed })}
              onClick={() => onChannelClick(ch.groupKey)}
            >
              <td className='align-left'>
                <span className='channel-name-cell'>
                  <ChannelIcon
                    channel={ch.channel}
                    platform={ch.platform}
                    level={hierarchyLevel}
                    size={16}
                  />
                  <InteractiveSpan
                    className='channel-link'
                    onClick={() => onChannelClick(ch.groupKey)}
                  >
                    {ch.displayName}
                  </InteractiveSpan>
                  {ch.hasIncrementalityTest && <IncrementalityBeaker />}
                  {ch.isDark && <span className='channel-tag'>Dark</span>}
                </span>
              </td>
              <td className='align-left'>
                <MediaTypeBadge mediaType={ch.mediaType} variant={MediaTypeBadgeVariant.FULL} />
              </td>
              <td className='align-center tabular'>
                <MetricCell
                  actualized={formatCurrency(ch.actualizedSpend)}
                  change={changeCurrency(ch.actualizedSpend, ch.directSpend)}
                />
              </td>
              <td className='align-center tabular'>
                <MetricCell
                  actualized={formatPercentage(ch.actualizedSpendShare, 1)}
                  change={changePercent(ch.actualizedSpendShare, ch.directSpendShare)}
                />
              </td>
              <td className='align-center tabular'>
                <MetricCell
                  actualized={formatRevenueValue(ch.actualizedContribution, isRevenue)}
                  change={changeRevenue(ch.actualizedContribution, ch.directContribution)}
                />
              </td>
              <td className='align-center tabular'>
                <MetricCell
                  actualized={formatPercentage(ch.actualizedContributionShare, 1)}
                  change={changePercent(ch.actualizedContributionShare, ch.directContributionShare)}
                />
              </td>
              <td
                className='align-center tabular efficiency-cell'
                style={
                  efficiencyHeatMap &&
                  ch.efficiencyActualized !== null &&
                  Number.isFinite(ch.efficiencyActualized)
                    ? { backgroundColor: efficiencyHeatMap(ch.efficiencyActualized) }
                    : undefined
                }
              >
                <MetricCell
                  actualized={formatEfficiency(ch.efficiencyActualized)}
                  change={changeEfficiency(ch.efficiencyActualized, ch.efficiencyDirect)}
                />
              </td>
            </tr>
          );
        })}
        {sorted.length === 0 && (
          <tr>
            <td colSpan={metricHeaders.length + 2} className='channel-performance-table-empty'>
              No channels in the selected window.
            </td>
          </tr>
        )}
      </tbody>
      <tfoot>
        <tr>
          <td className='align-left'>Total</td>
          <td aria-hidden='true' />
          <td className='align-center tabular'>
            <MetricCell actualized={formatCurrency(totals.actSpend)} change={null} />
          </td>
          <td aria-hidden='true' />
          <td className='align-center tabular'>
            <MetricCell
              actualized={formatRevenueValue(totals.actContribution, isRevenue)}
              change={null}
            />
          </td>
          <td aria-hidden='true' />
          <td className='align-center tabular'>
            <MetricCell actualized={formatEfficiency(totals.actEfficiency)} change={null} />
          </td>
        </tr>
      </tfoot>
    </PerformanceTableShell>
  );
};

interface MetricCellProps {
  actualized: string;
  change: string | null;
}

const MetricCell = ({ actualized, change }: MetricCellProps) => (
  <span className='metric-cell'>
    <span className='metric-value'>{actualized}</span>
    {change !== null && <span className='metric-change'>({change})</span>}
  </span>
);
